import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

import { IValidatedDataVM, IValidationViewModel, IFrequency, TagDateValue, Dictionary, IValueViewModel, IndexedValue, ReprocessingTaskOptionsOverride } from '../../models';
import { ValidatedValuesDto, LogbookEntryDto, ValueStateDto } from '../../models/DTO';
import { guidEmpty, momentFormat, wideService } from '../../common/constants';

import { IRequestService } from '../request.service';
import { ILogbookService } from './logbook.service';
import { toDictionary } from '../../common';

interface IValidationPageResultDto {
    validatedValuesDto: ValidatedValuesDto[];
    tagValues: TagDateValue[];
    childrenValues: ValueStateDto[];
    logbooks?: LogbookEntryDto[];
}

export interface IValidatedValuesService {
    save(inputList: ValidatedValuesDto[], syncReprocessing?: boolean, taskOptionsOverride?: ReprocessingTaskOptionsOverride, cancel?: angular.IPromise<Object>): angular.IPromise<number>;
    read(date: string, guidList: string[]): angular.IPromise<ValidatedValuesDto[]>;
    readAtOnce(
        date: string,
        identifiers: IValidationViewModel[],
        tagIdentifiers: IValidationViewModel[],
        childSelecteable: IValidationViewModel[],
        selectableIdentifiers: IValidationViewModel[],
        frequency: IFrequency,
        hasQuality: boolean,
        hasLogbook: boolean,
        force?: boolean
    ): angular.IPromise<IValidatedDataVM>;
}

export class ValidatedValuesService implements IValidatedValuesService {
    static serviceId = 'validatedValuesService';

    private _ctrlUrl: string;

    static $inject = ['requestService', 'logbookService'];

    constructor(
        private _requestService: IRequestService,
        private _logbookService: ILogbookService

    ) {
        this._ctrlUrl = 'validatedValues';
    }

    save(inputList: ValidatedValuesDto[], syncReprocessing?: boolean, taskOptionsOverride: ReprocessingTaskOptionsOverride = null, cancel?: angular.IPromise<Object>): angular.IPromise<number> {
        if (syncReprocessing === undefined) {
            syncReprocessing = false;
        }
        const url = `${this._ctrlUrl}/save/${syncReprocessing}`;
        return this._requestService.postAtV1(url, { values: inputList, taskOptionsOverride: taskOptionsOverride }, undefined, true, cancel);
    }

    read(date: string, guidList: string[]): angular.IPromise<ValidatedValuesDto[]> {
        const d = moment(date).toISOString();
        return this._requestService.postAtV1(this._ctrlUrl + `?dateTime=${d}`, guidList);
    }

    readAtOnce(
        date: string,
        identifiers: IValidationViewModel[],
        tagIdentifiers: IValidationViewModel[],
        childSelecteable: IValidationViewModel[],
        selectableIdentifiers: IValidationViewModel[],
        frequency: IFrequency,
        hasQuality: boolean,
        hasLogbook: boolean,
        force?: boolean
    ): angular.IPromise<IValidatedDataVM> {
        const config = {
            timeStamp: moment(date).toISOString(),
            validatedIdentifiers: identifiers.map(i => {
                return {
                    guid: i.guid,
                    selectableIdentifierGuids: i.selectableIdentifiers
                        ? i.selectableIdentifiers.map(d => d.guid)
                        : null

                };
            }),
            tagGuids: _.uniqBy(tagIdentifiers
                .filter(i => i.selectableIdentifiers.length > 0)
                .map(i => i.selectableIdentifiers[0])
                , t => t.guid).map(t => t.guid),
            childrenGuids: childSelecteable.map(i => i.guid),
            frequency,
            hasQuality,
            hasLogbook
        };

        let result = {} as unknown as IValidatedDataVM;
        const loadingData = {} as { [guid: string]: Dictionary<IndexedValue> };
        return this._requestService
            .cachedPostAtV1<IValidationPageResultDto>(`${this._ctrlUrl}/validationResults`, config, undefined, false, undefined, force)
            .then(data => {
                const vms = tagIdentifiers.concat(identifiers);
                result = this.mapValidatedDto(data.validatedValuesDto, result, vms);
                result.measuredValues = this.mapMeasuredValues({ values: data.tagValues }, tagIdentifiers, loadingData);
                this.mapChildren(data.childrenValues, selectableIdentifiers, loadingData);
                result.loadingData = loadingData;
                this.mapLogbookEntries(data.logbooks, result, date);
                return result;
            });
    }

    private mapValidatedDto(data: ValidatedValuesDto[], result: IValidatedDataVM, vms: IValueViewModel[]): IValidatedDataVM {
        const comments = {} as { [guid: string]: string };
        const selectedValues = {} as Dictionary<IndexedValue>;
        const manualValues = {} as Dictionary<IndexedValue>;

        data.forEach(d => {
            const isManual = d.source === guidEmpty && (d.manualValue || d.manualValue === 0);
            selectedValues[d.guid] = {
                comment: d.comment,
                formattedValue: String(d.value),
                source: isManual
                    ? 'manual'
                    : d.source,
                user: d.user,
                validationDate: d.user === wideService && !isManual
                    ? ''
                    : moment(d.validationDate).format(momentFormat),
                value: d.value,
            };
            if (d.manualValue || d.manualValue === 0) {
                manualValues[d.guid] = {
                    formattedValue: String(d.manualValue),
                    source: 'manual',
                    value: d.manualValue,
                };
            } else {
                manualValues[d.guid] = {
                    formattedValue: '-',
                    source: 'manual',
                    value: null,
                };
            }
            if (d.comment) {
                comments[d.guid] = d.comment;
            }
        });

        result.comments = comments;
        result.selectedValues = selectedValues;
        result.manualValues = manualValues;

        return result;
    }

    private mapMeasuredValues(
        data: { values: TagDateValue[] },
        tagIdentifiers: IValidationViewModel[],
        loadingData: Dictionary<Dictionary<IndexedValue>>
    ): Dictionary<IndexedValue> {
        // build index of data for each validated identifier viewmodel 
        // which has TAG strategy
        const measuredValues = {} as Dictionary<IndexedValue>;
        tagIdentifiers.forEach(v => {
            loadingData[v.guid] = {};
            // for each selectable, use group guid as id
            v.selectableIdentifiers.forEach(sel => {
                // search corresponding data
                const val = _.find(data.values, d => d.guid === sel.guid && d.valueType === v.valueType);
                measuredValues[v.guid] = {
                    formattedValue: val ? String(val.value) : '-',
                    quality: val ? val.quality : -1,
                    source: val ? val.guid : 'measured',
                    value: val?.value,
                };
            });
        });
        return measuredValues;
    }

    private mapChildren(
        data: ValueStateDto[],
        selectableIdentifiers: IValidationViewModel[],
        loadingData: Dictionary<Dictionary<IndexedValue>>
    ) {
        const index = toDictionary(data);
        // build index of data for each validated identifier viewmodel 
        // which has CHILDREN strategy
        selectableIdentifiers.forEach(v => {
            loadingData[v.guid] = {};
            // for each selectable, use group guid as id
            v.selectableIdentifiers.forEach(sel => {
                // search corresponding data
                const val = index[sel.guid];
                loadingData[v.guid][sel.groupList[0].guid] = {
                    formattedValue: val ? String(val.value) : '-',
                    quality: val ? val.quality : -1,
                    source: val ? val.guid : '-',
                    value: val?.value,
                };
            });
        });
    }

    private mapLogbookEntries(
        logbookEntries: LogbookEntryDto[],
        result: IValidatedDataVM,
        timestamp: string | Date
    ) {
        Object.keys(result.selectedValues).forEach(guid => {
            const selected = result.selectedValues[guid];
            const entries = this._logbookService.getEntriesRegarding(logbookEntries, guid);
            selected.badge = this._logbookService.getBadge(entries, timestamp);
        });
    }
}